import { HttpErrorResponse } from '@angular/common/http';
import { DateTime } from 'luxon';
import { v4 } from 'uuid';
import { HintDialogOption } from './../services/hint-dialog.service';
import { RouteStateService } from './../services/route-state.service';
import { AdvanceError } from './advance-error';

import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { Log } from '../models/log';

import { first, last } from 'lodash';
import { mapStackTrace } from 'sourcemapped-stacktrace';
import { LoggerService } from '../../common-api/tool-logger-api/api/logger.service';
import { getDescription } from '../../common/decorators/description.decorator';
import { ToastService } from '../../rt-shared/ui/toast/toast.service';
import { BusinessErrorEnum } from '../enums/business-error.enum';
import { HintDialogService } from '../services/hint-dialog.service';
import { isNullOrEmpty } from '../utils/common-functions';

declare type HitInfo = { hitCount: number; firstHitTime: Date };
const MAX_HIT_COUNT = 50;
const AVALAIBLE_SEND_DURATION = 5 * 60 * 1000;

/**
 * 錯誤處理服務
 *
 * @export
 * @class ErrorService
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  private logHitMap = new Map<string, HitInfo>();

  constructor(
    private logger: NGXLogger,
    private loggerService: LoggerService,
    private zone: NgZone,
    private toastService: ToastService,
    private hintDialogService: HintDialogService,
    private translateService: TranslateService,
    private routeStateService: RouteStateService,
  ) {}

  /**
   * dialog 顯示錯誤資訊
   *
   * @param {HttpErrorResponse} err
   * @memberof ErrorService
   */
  showBasicError(err: HttpErrorResponse): void {
    this.hintDialogService.showErrorHint(
      JSON.stringify({
        statusCode: err.status,
        message: err.message,
        error: err.error,
      })
    );
  }

  /**
   * 處理無權限錯誤
   *
   * @param {*} error
   * @memberof ErrorService
   */
  handleUnAuthorizedError(error: string | Error | undefined): void {
    const logErr = new AdvanceError(
      error,
      'Frontend error (Unauthorized)',
      undefined,
      window.location.href
    );
    this.writeLog('Frontend error (Unauthorized)', logErr);

    const msg = this.translateService.instant('Unauthorized');
    this.toastService.error(msg);

    this.routeStateService.navigateTo('/no-permission');
  }

  /**
   * 處理非預期的錯誤
   *
   * @param {*} error
   * @memberof ErrorService
   */
  handleUnknownError(error: string | Error | undefined): void {
    const logErr = new AdvanceError(
      error,
      'Frontend error (Unknown)',
      undefined,
      window.location.href,
    );

    this.writeLog('Frontend error (Unknown)', logErr);

    const msg = this.translateService.instant('Unknown Error');
    if (!error?.toString()?.includes('BusinessException'))
      this.toastService.error(msg);
  }

  /**
   * 處理 api 的錯誤
   *
   * @param {*} error
   * @param {string} clientTraceId
   * @memberof ErrorService
   */
  handleApiError(
    error: string | Error | undefined,
    clientTraceId: string
  ): void {
    const logErr = new AdvanceError(
      error,
      'Api error',
      undefined,
      this.routeStateService.currentUrl
    );

    if (
      globalThis.sigv.appContext.projectConfig.systemCode !=
      'RetailWebSubmission'
    ) {
      this.writeLog('Api error', logErr);
    }

    const msg = `Client-TraceId: ${clientTraceId}`;
    this.toastService.error(msg);
  }

  /**
   * 處理商業邏輯錯誤
   *
   * @param {string} errCode
   * @param {*} errorBody
   * @return {*}  {boolean}
   * @memberof ErrorService
   */
  handleBusinessError(errCode: string, errorBody: any): boolean {
    switch (errCode) {
      case 'FORMAT.RequestError':
        // Format errors
        const msgs = Object.values<any>(errorBody.errors)
          .reduce((sum, item) => sum.concat(item), [])
          .distinct();
        const hintOption: HintDialogOption = {
          headerIcon: 'pi pi-warning',
          message: msgs.length === 1 ? { category: msgs[0] } : {},
          messages:
            msgs.length > 1
              ? msgs.map((x: any) => ({ category: x }))
              : undefined,
          header: 'System Notification',
          hintType: msgs.length > 1 ? 'list' : 'message',
          cancelText: 'OK',
        };
        this.hintDialogService.showHint(hintOption);
        return true;
      case 'FORMAT.CustomError':
        return false;
      default:
        // Defined errors
        const prop = Object.getOwnPropertyNames(BusinessErrorEnum).find(
          (x) =>
            BusinessErrorEnum[x as keyof typeof BusinessErrorEnum] === errCode
        );
        if (isNullOrEmpty(prop)) {
          return false;
        }
        const msg = this.translateService.instant(
          getDescription(BusinessErrorEnum, prop!)
        );
        this.hintDialogService.showErrorHint(msg);
        return true;
    }
  }

  /**
   * 寫入 log
   *
   * @private
   * @param {string} title
   * @param {AdvanceError} logErr
   * @memberof ErrorService
   */
  private writeLog(title: string, logErr: AdvanceError): void {
    this.zone.run(() => {
      this.logger.error(logErr);
      // 對應 source map 程式路徑
      mapStackTrace(
        logErr.stack,
        (mappedStack: any[]) => {
          logErr.stack = mappedStack.join('\n');

          const clientTraceId = v4();
          const log = new Log(title, logErr.toString(), clientTraceId);
          this.logger.debug('Send log: ', log);
          this.writeApiLog(log);
        },
        { cacheGlobally: true }
      );
    });
  }

  /**
   * 呼叫 log api
   *
   * @private
   * @param {Log} log
   * @return {*}  {void}
   * @memberof ErrorService
   */
  private writeApiLog(log: Log): void {
    const now = DateTime.now().toJSDate();
    const logKey = log.body;

    // 清掉超過要檢查的時間區間的 log
    Array.from(this.logHitMap.entries()).forEach((entry) => {
      const key = first(entry) as string;
      const value = last(entry) as HitInfo;
      if (
        now.getTime() - value.firstHitTime.getTime() >
        AVALAIBLE_SEND_DURATION
      ) {
        this.logHitMap.delete(key);
      }
    });

    // 檢查是否可發送 log
    if (this.logHitMap.has(log.body)) {
      const hitInfo = this.logHitMap.get(logKey) as HitInfo;
      hitInfo.hitCount++;
      // 檢查是否超過重複發送的最大數量
      const exceedMaxHitCount = hitInfo.hitCount > MAX_HIT_COUNT;
      if (exceedMaxHitCount) {
        this.logger.info('This log hits the max send count.', log);
        return;
      }
    } else {
      this.logHitMap.set(logKey, { hitCount: 1, firstHitTime: now });
    }

    // 執行呼叫 log api
    this.loggerService.loggerErrorPost(log).subscribe({
      next: (result: any) => {},
      error: (err: any) => {
        this.logger.error(err);
      },
    });
  }
}
